import {
  customElement,
  DeesElement,
  property,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';
import './dotglobal-change-badge.js';

import type * as interfaces from '../../ts/ffb.interfaces.js';

interface ICommitRepoGroup {
  repoSlug: string;
  org: string;
  name: string;
  items: interfaces.IPublicCommit[];
}

/**
 * Activity side panel (foss.global v7 design): recent **commits** grouped by project. Each
 * repo group has a sticky header (org avatar, org/name, commit count, age) and a tree-indented
 * list of commit rows (conventional-commit kind badge, title, short SHA, short age). A commit
 * opens its own commit + diff (`/repo/<org>/<repo>/commits/<sha>`); the group header opens the
 * repository. Fed real per-commit data (`IPublicCommit[]`), so every entry links to its own
 * commit. Emits `dotglobal-close` and `dotglobal-navigate`.
 */
@customElement('dotglobal-activity-panel')
export class DotglobalActivityPanel extends DeesElement {
  @property({ type: Array })
  public accessor commits: interfaces.IPublicCommit[] = [];

  @property({ type: Object })
  public accessor orgAvatarUrls: Record<string, string> = {};

  private handleKeydown = (eventArg: KeyboardEvent) => {
    if (eventArg.key === 'Escape' && !eventArg.defaultPrevented) {
      this.emitClose();
    }
  };

  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        color: var(--text-primary, #0a0a0a);
        font-family: var(--font-sans);
      }

      * { box-sizing: border-box; }
      a { color: inherit; text-decoration: none; }
      button { border: 0; background: none; color: inherit; font: inherit; cursor: pointer; }
      dees-icon { display: inline-flex; flex-shrink: 0; }

      .actpanel {
        width: 384px;
        flex-shrink: 0;
        position: sticky;
        top: var(--nav-h, 52px);
        align-self: flex-start;
        height: calc(100vh - var(--nav-h, 52px));
        display: flex;
        flex-direction: column;
        border-left: 1px solid var(--border-subtle, #eeeeef);
        background: var(--bg-secondary, #f5f5f7);
      }

      .actpanel-h {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 14px 16px 0;
      }

      .title {
        display: flex;
        align-items: center;
        gap: 8px;
        font-family: var(--font-display);
        font-size: 15px;
        font-weight: 700;
      }

      .livedot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--success, #22c55e);
        flex-shrink: 0;
      }

      @media (prefers-reduced-motion: no-preference) {
        .livedot { animation: livepulse 2.4s ease-out infinite; }
      }

      @keyframes livepulse {
        0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.45); }
        70% { box-shadow: 0 0 0 7px rgba(34, 197, 94, 0); }
        100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
      }

      .grow { flex: 1; }

      .iconbtn {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        color: var(--text-secondary, #333333);
      }
      .iconbtn:hover {
        color: var(--text-primary, #0a0a0a);
        background: var(--hover, rgba(0, 0, 0, 0.04));
      }

      .actpanel-sub {
        padding: 5px 16px 13px;
        border-bottom: 1px solid var(--border-subtle, #eeeeef);
        color: var(--text-muted, #71717a);
        font-size: 12px;
        line-height: 1.5;
      }

      .actpanel-list {
        flex: 1;
        overflow-y: auto;
        overscroll-behavior: contain;
      }

      .actrepo { border-bottom: 1px solid var(--border-subtle, #eeeeef); }

      .actrepo-h {
        position: sticky;
        top: 0;
        z-index: 2;
        display: flex;
        align-items: center;
        gap: 9px;
        padding: 10px 16px;
        background: var(--bg-secondary, #f5f5f7);
        cursor: pointer;
        transition: background 0.12s;
      }
      .actrepo-h:hover { background: var(--row-hover, rgba(0, 122, 255, 0.06)); }

      .avatarbtn {
        display: inline-flex;
        padding: 0;
        border-radius: 6px;
        flex-shrink: 0;
      }

      .avatar {
        position: relative;
        width: 20px;
        height: 20px;
        display: inline-grid;
        place-items: center;
        flex-shrink: 0;
        border-radius: 6px;
        overflow: hidden;
        color: var(--accent-fg, #ffffff);
        background: var(--accent, #007aff);
        font-size: 8px;
        font-weight: 700;
      }
      .avatar img {
        position: absolute;
        inset: 0;
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .actrepo-slug {
        font-family: var(--font-mono);
        font-size: 12.5px;
        font-weight: 600;
        color: var(--text-primary, #0a0a0a);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .actrepo-org { color: var(--text-muted, #71717a); font-weight: 400; }
      .actrepo-h:hover .actrepo-slug { color: var(--accent, #007aff); }

      .actrepo-count {
        font-size: 11px;
        color: var(--text-muted, #71717a);
        flex-shrink: 0;
        white-space: nowrap;
      }
      .actrepo-age {
        font-size: 11px;
        color: var(--text-muted, #71717a);
        flex-shrink: 0;
        font-variant-numeric: tabular-nums;
        min-width: 26px;
        text-align: right;
      }

      .actrepo-changes { padding: 2px 0 8px; }

      .actchange {
        display: flex;
        align-items: baseline;
        gap: 8px;
        padding: 5px 16px 5px 18px;
        margin-left: 22px;
        border-left: 1px solid var(--border-subtle, #eeeeef);
        cursor: pointer;
        transition: background 0.12s, border-color 0.12s;
      }
      .actchange:hover {
        background: var(--row-hover, rgba(0, 122, 255, 0.06));
        border-left-color: var(--accent, #007aff);
      }

      .actchange dotglobal-change-badge {
        flex-shrink: 0;
        align-self: flex-start;
        margin-top: 1px;
      }

      .actchange-title {
        flex: 1;
        min-width: 0;
        font-size: 12.5px;
        line-height: 1.4;
        color: var(--text-primary, #0a0a0a);
        text-wrap: pretty;
      }

      .actsha {
        font-size: 10px;
        color: var(--text-muted, #71717a);
        border: 1px solid var(--border-default, #e4e4e7);
        border-radius: 5px;
        padding: 0 4px;
        flex-shrink: 0;
        align-self: flex-start;
        margin-top: 1px;
        font-family: var(--font-mono);
      }

      .actchange-age {
        font-size: 11px;
        color: var(--text-muted, #71717a);
        flex-shrink: 0;
        font-variant-numeric: tabular-nums;
        align-self: flex-start;
        margin-top: 1px;
      }

      .empty {
        padding: 48px 20px;
        text-align: center;
        color: var(--text-muted, #71717a);
      }

      @media (max-width: 1100px) {
        .actpanel {
          position: fixed;
          right: 0;
          top: var(--nav-h, 52px);
          z-index: 90;
          width: min(384px, 92vw);
          box-shadow: var(--shadow-lg, 0 4px 12px rgba(0, 0, 0, 0.15));
        }
      }
    `,
  ];

  public async connectedCallback(): Promise<void> {
    await super.connectedCallback();
    window.addEventListener('keydown', this.handleKeydown);
  }

  public async disconnectedCallback(): Promise<void> {
    window.removeEventListener('keydown', this.handleKeydown);
    await super.disconnectedCallback();
  }

  public render(): TemplateResult {
    const groups = this.groupByRepo();
    return html`
      <aside class="actpanel" data-screen-label="Activity panel">
        <div class="actpanel-h">
          <div class="title"><span class="livedot"></span>Activity</div>
          <span class="grow"></span>
          <button class="iconbtn" @click=${() => this.emitClose()} aria-label="Close activity panel">${this.icon('X')}</button>
        </div>
        <div class="actpanel-sub">Recent commits, grouped by project. Click a commit to open its diff.</div>
        <div class="actpanel-list">
          ${groups.length
            ? groups.map((group) => this.renderGroup(group))
            : html`<div class="empty">No recent commits loaded.</div>`}
        </div>
      </aside>
    `;
  }

  private renderGroup(groupArg: ICommitRepoGroup): TemplateResult {
    const newest = groupArg.items[0];
    return html`
      <div class="actrepo">
        <div class="actrepo-h" @click=${() => this.emitNavigate(`#/repo/${encodeURIComponent(groupArg.org)}/${encodeURIComponent(groupArg.name)}`)} title=${`Open ${groupArg.repoSlug}`}>
          <button class="avatarbtn" title="Open organization" @click=${(eventArg: Event) => { eventArg.stopPropagation(); this.emitNavigate(`#/org/${encodeURIComponent(groupArg.org)}`); }}>
            ${this.renderAvatar(groupArg.org, this.orgAvatarUrls[groupArg.org] || '')}
          </button>
          <span class="actrepo-slug"><span class="actrepo-org">${groupArg.org}/</span>${groupArg.name}</span>
          <span class="grow"></span>
          <span class="actrepo-count">${groupArg.items.length} commit${groupArg.items.length === 1 ? '' : 's'}</span>
          <span class="actrepo-age" title=${newest.createdAt}>${this.shortAge(newest.createdAt)}</span>
        </div>
        <div class="actrepo-changes">
          ${groupArg.items.map((commit) => this.renderCommit(groupArg, commit))}
        </div>
      </div>
    `;
  }

  private renderCommit(groupArg: ICommitRepoGroup, commitArg: interfaces.IPublicCommit): TemplateResult {
    const hash = `#/repo/${encodeURIComponent(groupArg.org)}/${encodeURIComponent(groupArg.name)}/commits/${encodeURIComponent(commitArg.sha)}`;
    return html`
      <div class="actchange" @click=${() => this.emitNavigate(hash)} title="Open commit & diff">
        <dotglobal-change-badge .kind=${commitArg.kind}></dotglobal-change-badge>
        <span class="actchange-title">${commitArg.title}</span>
        ${commitArg.shortSha ? html`<span class="actsha">${commitArg.shortSha}</span>` : ''}
        <span class="actchange-age" title=${commitArg.createdAt}>${this.shortAge(commitArg.createdAt)}</span>
      </div>
    `;
  }

  private groupByRepo(): ICommitRepoGroup[] {
    const groups: ICommitRepoGroup[] = [];
    const byRepo = new Map<string, ICommitRepoGroup>();
    const seen = new Set<string>();
    const sorted = [...this.commits].sort((commitA, commitB) => this.getTimestamp(commitB.createdAt) - this.getTimestamp(commitA.createdAt));
    for (const commit of sorted) {
      if (commit.sha && seen.has(commit.sha)) {
        continue;
      }
      if (commit.sha) {
        seen.add(commit.sha);
      }
      const repoSlug = commit.repoSlug;
      const [org, name] = repoSlug.split('/');
      if (!org || !name) {
        continue;
      }
      let group = byRepo.get(repoSlug);
      if (!group) {
        group = { repoSlug, org, name, items: [] };
        byRepo.set(repoSlug, group);
        groups.push(group);
      }
      group.items.push(commit);
    }
    return groups;
  }

  private emitClose(): void {
    this.dispatchEvent(new CustomEvent('dotglobal-close', { bubbles: true, composed: true }));
  }

  private emitNavigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true }));
  }

  private renderAvatar(labelArg: string, avatarUrlArg = ''): TemplateResult {
    return html`
      <span class="avatar">
        ${this.getInitials(labelArg)}
        ${avatarUrlArg
          ? html`<img src=${avatarUrlArg} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />`
          : ''}
      </span>
    `;
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${16}></dees-icon>`;
  }

  private getInitials(labelArg: string): string {
    const parts = labelArg.split(/[.\s/-]+/).filter(Boolean);
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'FG';
  }

  private shortAge(timestampArg: string): string {
    const diffMs = Date.now() - this.getTimestamp(timestampArg);
    if (!Number.isFinite(diffMs) || diffMs < 0) {
      return 'now';
    }
    const minutes = Math.round(diffMs / 60000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.round(hours / 24);
    if (days < 7) return `${days}d`;
    const weeks = Math.round(days / 7);
    if (days < 30) return `${weeks}w`;
    const months = Math.round(days / 30);
    if (months < 12) return `${months}mo`;
    return `${Math.round(days / 365)}y`;
  }

  private getTimestamp(timestampArg: string): number {
    const timestamp = new Date(timestampArg).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-activity-panel': DotglobalActivityPanel;
  }
}
